import filesize from 'filesize'

import emailIsValid from 'airship/modules/email-is-valid'
import urlIsValid from 'airship/modules/url-is-valid'
import {
  COLOR_HEXA_REGEX_6,
  COLOR_HEXA_REGEX_8,
} from 'airship/page/app-experience-settings/lib/constants'

import {ErrorType, ValidationError, ValidationHandler} from './types'

export interface RangeOptions {
  max?: number
  min?: number
  isRequired?: boolean
}
export interface LengthOptions {
  max: number
  min?: number
  null?: boolean
  stringLength?: (data: string) => number
}

export interface MagnitudeOptions {
  min: number
  max?: number
  null?: boolean
}

export function length(opts: LengthOptions): ValidationHandler<string> {
  return function validator(data?: string) {
    if (!data) {
      if (!opts.null) {
        throw new ValidationError('Field may not be blank', ErrorType.Required)
      }
      data = ''
    }

    const length: number = opts.stringLength
      ? opts.stringLength(data)
      : data.length

    if (length > opts.max) {
      throw new ValidationError('Field is too long', ErrorType.MaxLength)
    }
    if (length < (opts.min || 0)) {
      throw new ValidationError(
        `Field must contain at least ${opts.min} characters`,
        ErrorType.MinLength
      )
    }
  }
}

export function numberRange(opts: RangeOptions): ValidationHandler<number> {
  return function validator(data?: number) {
    if (data === undefined || isNaN(data)) {
      if (opts.isRequired) {
        throw new ValidationError(
          'Field may not be blank',
          ErrorType.MissingNumberValue
        )
      }
      return
    }

    if (typeof opts.max === 'number' && data > opts.max) {
      throw new ValidationError(`Max value ${opts.max}`, ErrorType.GetMessage)
    }

    if (typeof opts.min === 'number' && data < opts.min) {
      throw new ValidationError(`Min value ${opts.min}`, ErrorType.GetMessage)
    }
  }
}

export function requiredText(opts: LengthOptions): ValidationHandler<string> {
  return function validator(data?: string) {
    if (!data) {
      if (!opts.null) {
        throw new ValidationError(
          'Field may not be blank',
          ErrorType.TextRequired
        )
      }
      data = ''
    }

    const length: number = opts.stringLength
      ? opts.stringLength(data)
      : data.length

    if (length > opts.max) {
      throw new ValidationError('Field is too long', ErrorType.MaxLength)
    }
    if (length < (opts.min || 0)) {
      throw new ValidationError(
        `Field must contain at least ${opts.min} characters`,
        ErrorType.MinLength
      )
    }
  }
}

export function magnitude(opts: MagnitudeOptions): ValidationHandler<number> {
  return function validator(data?: number | null) {
    if (data === null || data === undefined || Number.isNaN(data)) {
      if (!opts.null) {
        throw new ValidationError('Field may not be empty', ErrorType.Required)
      }
      return
    }

    if (data < opts.min) {
      throw new ValidationError(
        `Field value must be at least ${opts.min}`,
        ErrorType.MinMagnitude
      )
    }
    if (opts.max && data > opts.max) {
      throw new ValidationError(
        `Field value must be at most ${opts.max}`,
        ErrorType.MaxMagnitude
      )
    }
  }
}

export function isValidKeypath(): ValidationHandler<string> {
  return function keypathValidator(data?: string) {
    // we're not checking for emptiness here.
    if (!data) return

    if (!data.match(/^[a-zA-Z$]/i)) {
      throw new ValidationError(
        'Field must start with a letter or $',
        ErrorType.InvalidKeypath
      )
    }

    if (data.indexOf(' ') !== -1) {
      throw new ValidationError(
        'Field does not allow spaces',
        ErrorType.NoSpacesAllowed
      )
    }

    const allowedCharactersPattern = /^[\w[\].$]+$/
    if (!data.match(allowedCharactersPattern)) {
      throw new ValidationError('Not a valid keypath', ErrorType.InvalidKeypath)
    }
  }
}

export function doesNotMatchItems(items: string[]): ValidationHandler<string> {
  return function duplicationListValidator(data?: string) {
    // we're not checking for emptiness here.
    if (!data) return

    if (items.includes(data.trim())) {
      throw new ValidationError(
        'This already exists',
        ErrorType.DuplicateExists
      )
    }
  }
}

export function isRequired(): ValidationHandler<string> {
  return function validator(data?: string) {
    if (!data) {
      throw new ValidationError('is required', ErrorType.Required)
    }
  }
}

export function isRequiredFile(): ValidationHandler<File> {
  return function validator(data?: File) {
    if (!data) {
      throw new ValidationError('is required', ErrorType.Required)
    }
  }
}

export function fileExceedsMaxSize(
  maxSizeBytes: number
): ValidationHandler<File> {
  return function validator(data?: File) {
    if (data && data.size > maxSizeBytes) {
      throw new ValidationError(
        `Your upload exceeds the maximum file size of ${filesize(
          maxSizeBytes
        )}`,
        ErrorType.Required
      )
    }
  }
}

export function isRequiredBoolean(): ValidationHandler<boolean> {
  return function validator(data?: boolean) {
    if (typeof data !== 'boolean') {
      throw new ValidationError('is required', ErrorType.Required)
    }
  }
}

export function isRequiredNumber(): ValidationHandler<number> {
  return function validator(data?: number) {
    if (typeof data !== 'number' || Number.isNaN(data)) {
      throw new ValidationError('is required', ErrorType.Required)
    }
  }
}

export function isRequiredStringArray(): ValidationHandler<string[]> {
  return function validator(data?: string[]) {
    if ((data?.length ?? 0) < 1) {
      throw new ValidationError('is required', ErrorType.Required)
    }
  }
}

export function noWhitespace(): ValidationHandler<string> {
  return function validator(data?: string) {
    // we're not checking for emptiness here.
    if (!data) return

    if (data.includes(' ') || data.includes('\t')) {
      throw new ValidationError(
        'contains whitespace',
        ErrorType.NoSpacesAllowed
      )
    }
  }
}

export function isToken(): ValidationHandler<string> {
  return function validator(data?: string) {
    // we're not checking for emptiness here.
    if (!data) return

    // being a "Token" means...
    // - is all lowercase
    // - starts with a character
    // - ends with a character or number
    // - may contain interior underscores
    // - no whitespace

    if (/[A-Z]/.test(data)) {
      throw new ValidationError(
        'must be all lowercase',
        ErrorType.NoUppercaseLettersAllowed
      )
    }

    if (!/^[a-z]/.test(data)) {
      throw new ValidationError(
        'must start with a letter',
        ErrorType.MustStartWithLetter
      )
    }
    if (!/[a-z0-9]$/.test(data)) {
      throw new ValidationError(
        'must end with a letter or number',
        ErrorType.MustEndWithLetterOrNumber
      )
    }
    if (data.includes(' ') || data.includes('\t')) {
      throw new ValidationError(
        'contains whitespace',
        ErrorType.NoSpacesAllowed
      )
    }
    if (!/^\w+$/.test(data)) {
      throw new ValidationError(
        'invalid special character, only underscores are allowed',
        ErrorType.InternalUnderscoresOnly
      )
    }
  }
}

export function noAirshipNamespace(): ValidationHandler<string> {
  return function validator(data?: string) {
    // we're not checking for emptiness here.
    if (!data) return

    if (data.startsWith('ua_')) {
      throw new ValidationError(
        'must not begin with "ua_"',
        ErrorType.AirshipReservedNamespace
      )
    }
  }
}

export function isValidHexColor(
  allow8Char?: boolean
): ValidationHandler<string> {
  return function validator(data?: string) {
    if (!data) return
    const isValidHexColor = (allow8Char
      ? COLOR_HEXA_REGEX_8
      : COLOR_HEXA_REGEX_6
    ).test(data)
    if (!isValidHexColor) {
      throw new ValidationError('Color is invalid', ErrorType.GetMessage)
    }
  }
}

export function isRequiredColor(): ValidationHandler<string> {
  return function validator(data?: string) {
    if (!data) {
      throw new ValidationError('Color is required', ErrorType.GetMessage)
    }
  }
}

export function mustNotBeginWith(
  startString: string
): ValidationHandler<string> {
  return function validator(data?: string) {
    if (!data) return
    const isValid = !data.startsWith(startString)
    if (!isValid) {
      throw new ValidationError(
        `must not begin with "${startString}"`,
        ErrorType.GetMessage
      )
    }
  }
}

export function noMoreThanFiveElements(): ValidationHandler<string> {
  return function validator(data?: string) {
    // we're not checking for emptiness nor null here.
    if (!data) return

    const elements = data.split(',')
    if (elements.length > 5) {
      throw new ValidationError(
        'Max elements allowed is five',
        ErrorType.GetMessage
      )
    }
  }
}

export function isValidUrl(): ValidationHandler<string> {
  return function validator(data?: string) {
    if (data && !urlIsValid(data)) {
      throw new ValidationError(
        'This must be a valid URL.',
        ErrorType.InvalidURL
      )
    }
  }
}

export function isValidEmail(): ValidationHandler<string> {
  return function validator(data?: string) {
    if (!data) data = ''

    if (!emailIsValid(data)) {
      throw new ValidationError(
        'This must be a valid email address.',
        ErrorType.InvalidEmail
      )
    }
  }
}
